import React, { useMemo } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Paper,
    Stack,
    Switch,
    Typography,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import DebouncedSearchInput from "./DebouncedSearchInput";
import SortBySelect from "./SortBySelect";
import FilterByStatus, { type StatusFilter } from "./FilterByStatus";
import CollectionPagination from "./CollectionPagination";
import type { UseListStateReturn } from "../../hooks/useListState";

// Re-export StatusFilter for convenience
export type { StatusFilter } from "./FilterByStatus";

/**
 * Column configuration for RubricManagementList
 */
export interface Column<T> {
    /** Field name used for sorting */
    field: string;
    /** Display label for the column header */
    label: string;
    /** Whether this column is sortable (default: true) */
    sortable?: boolean;
    /** Column width - number for fixed px, "flex" for flexible */
    width?: number | string;
    /** Custom render function for cell content */
    render?: (item: T) => React.ReactNode;
}

/**
 * Props for RubricManagementList component
 */
export interface RubricManagementListProps<T> {
    /** Array of items to display */
    items: T[];
    /** Total count of items (for pagination) */
    totalCount: number;
    /** Whether data is currently loading */
    loading: boolean;
    /** Error message to display, if any */
    error: string | null;

    /** List state from useListState hook */
    listState: UseListStateReturn;

    /** Column configuration */
    columns: Column<T>[];
    /** Function to get unique key for each item */
    getItemKey: (item: T) => string | number;

    /** Callback when an item row is clicked */
    onItemClick: (item: T) => void;
    /** Callback when add button is clicked */
    onAddClick: () => void;

    /** Placeholder text for search input */
    searchPlaceholder: string;
    /** Label for the add button */
    addButtonLabel: string;
    /** Label for pagination (e.g., "answer sets", "questions") */
    itemLabel: string;
    /** Message to show when no items exist */
    emptyMessage: string;

    /** Optional render function for action buttons per row */
    renderActions?: (item: T) => React.ReactNode;

    /** Optional status filter value (default: "all") */
    statusFilter?: StatusFilter;
    /** Optional callback when status filter changes */
    onStatusFilterChange?: (value: StatusFilter) => void;

    /** Optional callback when active toggle is changed - if provided, shows toggle on left side */
    onToggleActive?: (item: T, active: boolean) => void;
    /** Function to get the active status of an item - required if onToggleActive is provided */
    isItemActive?: (item: T) => boolean;
}

/**
 * Reusable list component for rubric management panels.
 * Provides search, sort-by select dropdown, pagination, and consistent styling.
 * Implements no-flicker loading pattern.
 */
function RubricManagementList<T>({
    items,
    totalCount,
    loading,
    error,
    listState,
    columns,
    getItemKey,
    onItemClick,
    onAddClick,
    searchPlaceholder,
    addButtonLabel,
    itemLabel,
    emptyMessage,
    renderActions,
    statusFilter,
    onStatusFilterChange,
    onToggleActive,
    isItemActive,
}: RubricManagementListProps<T>): React.ReactElement {
    const showActiveToggle = Boolean(onToggleActive && isItemActive);
    // Determine if this is the initial load (no data yet)
    const isInitialLoad = loading && items.length === 0 && !listState.search;
    // Determine if we're refetching (have data, but loading new data)
    const isRefetching = loading && items.length > 0;
    // No results from search
    const noSearchResults = !loading && items.length === 0 && !!listState.search;

    const sortOptions = useMemo(
        () => columns
            .filter((column) => column.sortable !== false)
            .map((column) => ({ field: column.field, label: column.label })),
        [columns]
    );

    const handleSortFieldChange = (field: string) => {
        listState.setSort(field, listState.sortDirection);
    };

    const handleToggleSortDirection = () => {
        listState.toggleSort(listState.sortBy);
    };

    return (
        <Box>
            {/* Search bar, Sort by select, and Add button */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={2}>
                <DebouncedSearchInput
                    value={listState.searchInput}
                    onChange={listState.setSearchInput}
                    placeholder={searchPlaceholder}
                />
                <SortBySelect
                    options={sortOptions}
                    sortBy={listState.sortBy}
                    sortDirection={listState.sortDirection}
                    onSortChange={handleSortFieldChange}
                    onDirectionToggle={handleToggleSortDirection}
                />
                {onStatusFilterChange && (
                    <FilterByStatus
                        value={statusFilter ?? "all"}
                        onChange={onStatusFilterChange}
                    />
                )}
                <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={onAddClick}
                        sx={{ whiteSpace: "nowrap", height: "100%" }}
                    >
                        {addButtonLabel}
                    </Button>
                </Box>
            </Stack>
            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {/* Content Area */}
            {isInitialLoad ? (
                // Initial load: show centered spinner
                (<Stack alignItems="center" py={6}>
                    <CircularProgress />
                </Stack>)
            ) : noSearchResults ? (
                // No items but search active: show no results message
                (<Box
                    sx={{
                        mt: 2,
                        p: 3,
                        textAlign: "center",
                        backgroundColor: "#F6FBFF",
                        borderRadius: 2,
                        border: "1px solid #BBDCFB"                    
                    }}
                    data-testid= "no-results-container"
                >
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                        No results found for "{listState.search}"
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={listState.reset}
                        sx={{ mt: 1 }}
                    >
                        Clear search
                    </Button>
                </Box>)
            ) : items.length === 0 ? (
                // No items and no search: show empty message
                (<Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                </Typography>)
            ) : (
                // Has items: show list with optional loading overlay
                (<>
                    <Box
                        sx={{
                            position: "relative",
                            opacity: isRefetching ? 0.6 : 1,
                            transition: "opacity 0.2s ease-in-out",
                        }}
                    >
                        <Stack spacing={2} width="100%">
                            {items.map((item) => (
                                <Paper
                                    key={getItemKey(item)}
                                    variant="outlined"
                                    sx={{
                                        cursor: "pointer",
                                        px: 2,
                                        py: 1.5,
                                        backgroundColor: "#F6FBFF",
                                        borderColor: "#BBDCFB",
                                        borderRadius: "3px",
                                        width: "100%",
                                    }}
                                    onClick={() => onItemClick(item)}
                                >
                                    <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                        {showActiveToggle && onToggleActive && isItemActive && (
                                            <Box
                                                sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Switch
                                                    checked={isItemActive(item)}
                                                    onChange={(event) => onToggleActive(item, event.target.checked)}
                                                    size="small"
                                                    sx={{
                                                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#F28D7A" },
                                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#F9C6BA" },
                                                    }}
                                                />
                                            </Box>
                                        )}
                                        {columns.map((column) => (
                                            <Box
                                                key={column.field}
                                                sx={{
                                                    flex:
                                                        column.width === "flex" || column.width === undefined
                                                            ? 1
                                                            : undefined,
                                                    width: typeof column.width === "number" ? column.width : undefined,
                                                    minWidth: 0,
                                                }}
                                            >
                                                {column.render ? (
                                                    column.render(item)
                                                ) : (
                                                    <Typography variant="body1" noWrap>
                                                        {String((item as Record<string, unknown>)[column.field] ?? "—")}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                        {renderActions && (
                                            <Box
                                                sx={{ width: 80, display: "flex", justifyContent: "flex-end" }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {renderActions(item)}
                                            </Box>
                                        )}
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    </Box>
                    <CollectionPagination
                        count={totalCount}
                        page={listState.page}
                        rowsPerPage={listState.pageSize}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        onPageChange={listState.setPage}
                        onRowsPerPageChange={listState.setPageSize}
                        itemLabel={itemLabel}
                    />
                </>)
            )}
        </Box>
    );
}

export default RubricManagementList;
